/**
 * HeroArt — the two decorative illustrations for the storefront header.
 *
 *   • CampusBackdrop — a faint college skyline + clouds, sits BEHIND the hero
 *     text as a watermark (deeper-gold shapes over the golden gradient).
 *   • DeliveryScooter — a flat-illustration rider on a yellow scooter carrying
 *     a "ROCKET07 COLLEGE ESSENTIALS" delivery box. The ROCKET07 label uses the
 *     DEEP end of the brand orange: the logo's bright #fb7307 scores only 1.53
 *     contrast against the amber box and all but vanishes at 9px, while
 *     #c2410c still reads unmistakably orange.
 *
 * Both are pure inline SVG (no external assets) so they stay crisp at any size
 * and re-colour with the brand. Everything is aria-hidden — purely decorative.
 */

import { useState } from 'react';

/** Where the watermark sits — shared so the photo and the SVG line up exactly. */
const WATERMARK_BOX = 'pointer-events-none absolute inset-x-0 bottom-0 h-[82%] w-full';

/**
 * CampusWatermark — the silhouette behind the hero.
 *
 * Shows the student's OWN campus when a super admin has set a photo for their
 * college, and the generic skyline otherwise. A college without a photo, or
 * one whose photo fails to load, must still get a finished-looking hero — so
 * the illustration is the fallback in both cases, not a blank space.
 *
 * The photo is deliberately not shown as a photo. `saturate-0` strips it to
 * grey and `mix-blend-multiply` lets the gold gradient behind it show through,
 * so the building reads as a shadow cast on the hero rather than a picture
 * pasted onto it — whatever the original colours were. The mask fades it out
 * before it reaches the headline, which is what keeps the text readable over
 * an image nobody has vetted for contrast.
 */
export function CampusWatermark({ college }) {
  const [failed, setFailed] = useState(false);
  const src = college?.heroImage;

  if (!src || failed) return <CampusBackdrop className={WATERMARK_BOX} />;

  return (
    <div className={`${WATERMARK_BOX} overflow-hidden`} aria-hidden="true">
      <img
        src={src}
        alt=""
        onError={() => setFailed(true)}
        className="h-full w-full object-cover object-bottom opacity-[0.55] saturate-0 mix-blend-multiply"
        style={{
          // Solid a little higher than before so the building itself reads
          // clearly, while the fade still lands well below the headline —
          // that top falloff is what protects the text, not the opacity.
          maskImage: 'linear-gradient(to top, #000 58%, transparent 95%)',
          WebkitMaskImage: 'linear-gradient(to top, #000 58%, transparent 95%)',
        }}
      />
    </div>
  );
}

export function CampusBackdrop({ className }) {
  return (
    <svg
      className={className}
      viewBox="0 0 400 220"
      fill="none"
      preserveAspectRatio="xMidYMax slice"
      aria-hidden="true"
    >
      {/* Soft clouds near the top */}
      <g fill="#ffffff" opacity="0.4">
        <ellipse cx="58" cy="38" rx="34" ry="15" />
        <ellipse cx="94" cy="44" rx="24" ry="12" />
        <ellipse cx="332" cy="30" rx="30" ry="13" />
        <ellipse cx="302" cy="38" rx="22" ry="10" />
      </g>

      {/* Campus buildings — faint, a touch deeper than the background gold */}
      <g opacity="0.5">
        {/* side domes */}
        <path d="M40 122 a46 30 0 0 1 92 0 Z" fill="#ecbe4c" />
        <path d="M268 120 a48 30 0 0 1 96 0 Z" fill="#ecbe4c" />
        {/* left & right wings */}
        <rect x="40" y="120" width="92" height="90" fill="#eec152" />
        <rect x="268" y="118" width="96" height="92" fill="#eec152" />
        {/* centre block + pediment */}
        <polygon points="148,106 200,74 252,106" fill="#f2ca60" />
        <rect x="150" y="104" width="100" height="106" fill="#f2ca60" />
        {/* clock tower + flag */}
        <polygon points="185,54 200,34 215,54" fill="#f5d275" />
        <rect x="187" y="52" width="26" height="34" fill="#f5d275" />
        <rect x="199" y="18" width="2.5" height="18" fill="#dca400" />
        <polygon points="201,18 215,23 201,29" fill="#e6a838" />
        {/* arched windows */}
        <g fill="#fdeaad" opacity="0.75">
          <rect x="56" y="140" width="14" height="26" rx="7" />
          <rect x="80" y="140" width="14" height="26" rx="7" />
          <rect x="104" y="140" width="14" height="26" rx="7" />
          <rect x="286" y="138" width="14" height="26" rx="7" />
          <rect x="310" y="138" width="14" height="26" rx="7" />
          <rect x="334" y="138" width="14" height="26" rx="7" />
          <rect x="164" y="128" width="16" height="30" rx="8" />
          <rect x="220" y="128" width="16" height="30" rx="8" />
        </g>
        {/* grand entrance */}
        <path d="M190 210 v-46 a10 10 0 0 1 20 0 v46 Z" fill="#e4b446" />
      </g>

      {/* Trees on the far right */}
      <g fill="#e2c454" opacity="0.5">
        <circle cx="380" cy="150" r="22" />
        <circle cx="393" cy="168" r="16" />
        <circle cx="366" cy="170" r="14" />
      </g>
    </svg>
  );
}

export function DeliveryScooter({ className }) {
  return (
    <svg className={className} viewBox="0 0 260 210" fill="none" aria-hidden="true">
      {/* motion lines */}
      <g stroke="#ffffff" strokeWidth="3.5" strokeLinecap="round" opacity="0.55">
        <path d="M16 118 H44" />
        <path d="M8 140 H38" />
        <path d="M18 162 H46" />
      </g>

      {/* Delivery box */}
      <polygon
        points="44,96 56,84 110,84 98,96"
        fill="#ffcf5e"
        stroke="#c98713"
        strokeWidth="2"
        strokeLinejoin="round"
      />
      <rect
        x="44"
        y="96"
        width="54"
        height="52"
        rx="5"
        fill="#f4b62e"
        stroke="#c98713"
        strokeWidth="2"
      />
      <text
        x="71"
        y="116"
        textAnchor="middle"
        fontSize="9"
        fontWeight="800"
        fill="#c2410c"
        fontFamily="Arial, sans-serif"
      >
        ROCKET07
      </text>
      <text
        x="71"
        y="130"
        textAnchor="middle"
        fontSize="6"
        fontWeight="700"
        fill="#a06a12"
        fontFamily="Arial, sans-serif"
      >
        COLLEGE
      </text>
      <text
        x="71"
        y="139"
        textAnchor="middle"
        fontSize="6"
        fontWeight="700"
        fill="#a06a12"
        fontFamily="Arial, sans-serif"
      >
        ESSENTIALS
      </text>

      {/* Wheels */}
      <g>
        <circle cx="88" cy="172" r="26" fill="#303538" />
        <circle cx="88" cy="172" r="14" fill="#cfd4d7" />
        <circle cx="88" cy="172" r="5" fill="#6b7075" />
        <circle cx="200" cy="172" r="26" fill="#303538" />
        <circle cx="200" cy="172" r="14" fill="#cfd4d7" />
        <circle cx="200" cy="172" r="5" fill="#6b7075" />
      </g>

      {/* Scooter body */}
      <path
        d="M60 170 Q56 138 82 134 L112 134 Q124 134 124 148 L124 170 Z"
        fill="#f7c948"
        stroke="#e0a800"
        strokeWidth="2"
        strokeLinejoin="round"
      />
      <rect
        x="108"
        y="150"
        width="82"
        height="13"
        rx="5"
        fill="#f7c948"
        stroke="#e0a800"
        strokeWidth="2"
      />
      <polygon
        points="182,152 196,104 212,108 202,152"
        fill="#f7c948"
        stroke="#e0a800"
        strokeWidth="2"
        strokeLinejoin="round"
      />
      <path
        d="M176 168 A24 24 0 0 1 224 168 L216 168 A20 20 0 0 0 184 168 Z"
        fill="#f7c948"
        stroke="#e0a800"
        strokeWidth="2"
        strokeLinejoin="round"
      />
      <circle cx="206" cy="114" r="5" fill="#fff2c2" />

      {/* Seat */}
      <rect x="76" y="120" width="54" height="15" rx="7" fill="#33383b" />

      {/* Handlebar */}
      <path d="M204 108 L214 86" stroke="#33383b" strokeWidth="5" strokeLinecap="round" />
      <circle cx="215" cy="85" r="4" fill="#33383b" />

      {/* Rider — far leg */}
      <path
        d="M112 126 L138 152 L150 170"
        fill="none"
        stroke="#24413f"
        strokeWidth="13"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <ellipse cx="154" cy="172" rx="9" ry="5" fill="#1f2a2a" />

      {/* Rider — torso (shirt) */}
      <path d="M113 124 L128 88" stroke="#f7c948" strokeWidth="22" strokeLinecap="round" />

      {/* Rider — near leg */}
      <path
        d="M116 126 L142 154 L158 170"
        fill="none"
        stroke="#2d524f"
        strokeWidth="13"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <ellipse cx="162" cy="172" rx="9" ry="5" fill="#26302f" />

      {/* Rider — arm reaching to handlebar */}
      <path d="M127 90 L170 100" stroke="#f7c948" strokeWidth="12" strokeLinecap="round" />
      <path d="M170 100 L207 108" stroke="#eec19a" strokeWidth="9.5" strokeLinecap="round" />

      {/* Rider — head + helmet */}
      <circle cx="135" cy="72" r="13" fill="#eec19a" />
      <path
        d="M121 74 Q120 54 136 54 Q152 54 151 73 Q147 62 136 62 Q124 62 121 74 Z"
        fill="#f7c948"
        stroke="#e0a800"
        strokeWidth="1.5"
      />
      <path
        d="M121 74 Q124 68 130 68"
        stroke="#e0a800"
        strokeWidth="2"
        strokeLinecap="round"
        fill="none"
      />
    </svg>
  );
}
